/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { Table } from 'react-bootstrap-v5'
import { useParams, Link, useHistory } from 'react-router-dom'
import { KTSVG } from '../../_metronic/helpers'
import Swal from 'sweetalert2'
import GetPagePermission from '../components/GetPagePermission'
import { formatDecimalQuantity } from '../helpers/GlobalHelper'
import { AppContext } from '../context/AppContext'
import EditActualProductionQtyModal from '../components/EditActualProductionQtyModal'

interface RouteParams {
	id: string;
}

interface QcTestRow {
	idQcTests: number;
	testName: string;
	method: string;
	limitFrom: string;
	limitTo: string;
	result: string;
	note: string;
}

interface TimelineEvent {
	type: 'status' | 'packing' | 'edit';
	key: string;
	title: string;
	createdAt: string;
	userName: string | null;
	idPackingOrders?: number;
	packingDate?: string;
	quantity?: number;
	oldValue?: number | null;
	newValue?: number | null;
	oldRatePerUnit?: number | null;
	newRatePerUnit?: number | null;
	reason?: string | null;
}

const timelineStyle: Record<TimelineEvent['type'], { icon: string; color: string }> = {
	status: { icon: '/media/svg/icons/Navigation/Check.svg', color: 'primary' },
	packing: { icon: '/media/svg/icons/Shopping/Box2.svg', color: 'success' },
	edit: { icon: '/media/svg/icons/Design/Edit.svg', color: 'warning' },
}

const formatDateTime = (value: string) => {
	const date = new Date(value.replace(' ', 'T'))
	return isNaN(date.getTime()) ? value : `${date.toDateString()}, ${date.toLocaleTimeString()}`
}

const displayActualQuantity = (row: any) => {
	if (row.actualQuantity === null || row.actualQuantity === undefined || row.actualQuantity === '') {
		return row.quantity
	}
	return row.actualQuantity
}

const variance = (row: any) => {
	if (row.isQcAdded) {
		return ''
	}
	const actual = parseFloat(String(displayActualQuantity(row)))
	const bom = parseFloat(String(row.quantity))
	if (isNaN(actual) || isNaN(bom)) {
		return ''
	}
	return formatDecimalQuantity(actual - bom)
}

const variancePercent = (row: any) => {
	if (row.isQcAdded) {
		return ''
	}
	const actual = parseFloat(String(displayActualQuantity(row)))
	const bom = parseFloat(String(row.quantity))
	if (isNaN(actual) || isNaN(bom) || bom === 0) {
		return ''
	}
	return formatDecimalQuantity(((actual - bom) / bom) * 100, 2)
}

const sumActualWeight = (materialRows: any[]): number | null => {
	if (materialRows.length === 0) {
		return null
	}
	let sum = 0
	for (const row of materialRows) {
		const actual = parseFloat(String(displayActualQuantity(row)))
		if (isNaN(actual)) {
			return null
		}
		sum += actual
	}
	return parseFloat(sum.toFixed(5))
}

function BlendingOrderDetails() {
	const { id }: RouteParams = useParams()
	const history = useHistory()
	const [order, setOrder] = useState<any>(null)
	const [rows, setRows] = useState<any[]>([])
	const [qcTests, setQcTests] = useState<QcTestRow[]>([])
	const [activeTab, setActiveTab] = useState<'materials' | 'qc'>('materials')
	const [timeline, setTimeline] = useState<TimelineEvent[]>([])
	const [showEditQty, setShowEditQty] = useState(false)
	const canApprovePage = GetPagePermission('/approve-blending-orders')
	const { currentUser } = useContext(AppContext)

	const loadOrder = () => {
		fetch(process.env.REACT_APP_API_URL + 'blending-orders/' + id, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				setOrder(resp.data)
				setRows(resp.rows || [])
				setTimeline(resp.timeline || [])
				setQcTests((resp.qcTests || []).map((row: QcTestRow) => ({
					...row,
					limitFrom: row.limitFrom ?? '',
					limitTo: row.limitTo ?? '',
					result: row.result ?? '',
					note: row.note ?? '',
				})))
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}

	useEffect(() => {
		if (id) {
			loadOrder()
		}
	}, [id])

	const postAction = async (url: string, confirmText: string, confirmButtonText: string, afterSuccess?: () => void) => {
		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: confirmText,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText,
		})
		if (!confirm.isConfirmed) {
			return
		}

		try {
			const response = await fetch(url, postRequestOptions({}))
			const data = await response.json()
			if (data.success) {
				Swal.fire({
					icon: 'success',
					title: 'Success',
					text: data.successMessage,
				}).then(() => {
					if (afterSuccess) {
						afterSuccess()
					} else {
						loadOrder()
					}
				})
			} else {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: data.errorMessage || data.errors || 'Action failed.',
				})
			}
		} catch (error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'An error occurred.',
			})
		}
	}

	const qcVisible = order?.status === 'QC' || order?.status === 'QC Passed'
	const actualWeightInKg = useMemo(() => sumActualWeight(rows), [rows])

	// same roles that see packing costs, plus whoever may edit the quantity
	const canViewCost = Number(currentUser?.id_roles) === 3 || Number(currentUser?.id_roles) === 4 || Boolean(order?.canEditActualQuantity)

	if (!order) {
		return (
			<div className="card card-custom">
				<div className="card-body">Loading...</div>
			</div>
		)
	}

	return (
		<div className="card card-custom">
			<div className="card-header">
				<div className="card-title">
					<KTSVG path="/media/svg/icons/Text/Bullet-list.svg" className="svg-icon-1 svg-icon-primary" />
					<span className="text fw-bolder" style={{ marginLeft: '10px' }}>
						Blending Order Details
					</span>
				</div>
				<div className="card-toolbar">
					{order.canEdit &&
						<Link to={`/blending-order-edit/${id}`} className="btn btn-sm btn-primary me-2">Edit</Link>
					}
					{order.canEditActualQuantity &&
						<button type="button" className="btn btn-sm btn-primary me-2" onClick={() => setShowEditQty(true)}>
							Edit Actual Qty
						</button>
					}
					{order.status === 'Draft' && canApprovePage === 1 &&
						<button
							type="button"
							className="btn btn-sm btn-success me-2"
							onClick={() => postAction(
								`${process.env.REACT_APP_API_URL}blending-orders/approve/${id}`,
								'You want to approve this blending order?',
								'Yes, Approve'
							)}
						>
							Approve
						</button>
					}
					{order.status === 'Approved' &&
						<button
							type="button"
							className="btn btn-sm btn-warning me-2"
							onClick={() => postAction(
								`${process.env.REACT_APP_API_URL}blending-orders/start-wip/${id}`,
								'This will stock out required materials and move the order to WIP.',
								'Yes, Start WIP',
								() => history.push('/work-in-progress')
							)}
						>
							Start WIP
						</button>
					}
					<Link to="/blending-orders" className="btn btn-sm btn-secondary">Back</Link>
				</div>
			</div>
			<div className="card-body">
				<Table size="sm" bordered>
					<tbody>
						<tr>
							<td width="30%">Production Order No</td>
							<td> : {order.productionOrderNo}</td>
						</tr>
						<tr>
							<td>Status</td>
							<td> : {order.status}</td>
						</tr>
						<tr>
							<td>Project</td>
							<td> : {order.projectName}</td>
						</tr>
						<tr>
							<td>Production Date</td>
							<td> : {order.productionDate}</td>
						</tr>
						<tr>
							<td>Product Name</td>
							<td> : {order.itemName}</td>
						</tr>
						<tr>
							<td>Product Code</td>
							<td> : {order.itemCode}</td>
						</tr>
						<tr>
							<td>BOM</td>
							<td> : {order.bomName}</td>
						</tr>
						<tr>
							<td>Production Quantity (Ltr)</td>
							<td>
								{' : Qty: '}{formatDecimalQuantity(order.productionQuantity, 2)} {order.unitName}
								{qcVisible && (
									<>
										{' | Actual Qty: '}
										{formatDecimalQuantity(
											order.actualProductionQuantity !== null
												&& order.actualProductionQuantity !== undefined
												&& order.actualProductionQuantity !== ''
												? order.actualProductionQuantity
												: order.productionQuantity,
											2
										)}{' '}{order.unitName}
									</>
								)}
							</td>
						</tr>
						{canViewCost && order.ratePerUnit !== null && order.ratePerUnit !== undefined &&
							<tr>
								<td>Cost per {order.unitName || 'Unit'}</td>
								<td> : {formatDecimalQuantity(order.ratePerUnit, 4)}</td>
							</tr>
						}
						{qcVisible &&
							<>
								<tr>
									<td>Already Used</td>
									<td> : {formatDecimalQuantity(order.alreadyUsed)}</td>
								</tr>
								<tr>
									<td>Remaining Quantity</td>
									<td> : {formatDecimalQuantity(order.remainingQty)}</td>
								</tr>
							</>
						}
						<tr>
							<td>Weight in KG</td>
							<td>
								{' : Qty: '}{formatDecimalQuantity(order.weightInKg)}
								{qcVisible && (
									<>
										{' | Actual Qty: '}
										{actualWeightInKg === null
											? formatDecimalQuantity(order.weightInKg)
											: formatDecimalQuantity(actualWeightInKg)}
									</>
								)}
							</td>
						</tr>
						<tr>
							<td>Batch No</td>
							<td> : {order.batchNo}</td>
						</tr>
						<tr>
							<td>Remarks</td>
							<td style={{ whiteSpace: 'pre-wrap' }}> : {order.remarks}</td>
						</tr>
						<tr>
							<td>Created By</td>
							<td> : {order.createdBy}</td>
						</tr>
					</tbody>
				</Table>

				{qcVisible
					? (
						<>
							<ul className="nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5">
								<li className="nav-item">
									<button
										type="button"
										className={`nav-link${activeTab === 'materials' ? ' active' : ''}`}
										onClick={() => setActiveTab('materials')}
									>
										Required Materials
									</button>
								</li>
								<li className="nav-item">
									<button
										type="button"
										className={`nav-link${activeTab === 'qc' ? ' active' : ''}`}
										onClick={() => setActiveTab('qc')}
									>
										QC Test
									</button>
								</li>
							</ul>

							{activeTab === 'materials' &&
								<Table size="sm" bordered hover>
									<thead>
										<tr>
											<th>SN</th>
											<th>Item Name</th>
											<th>Category</th>
											<th>Percentage</th>
											<th>Quantity</th>
											<th>Unit</th>
											<th>Warehouse</th>
											<th>Actual Qty</th>
											<th>Variance</th>
											<th>Variance %</th>
										</tr>
									</thead>
									<tbody>
										{rows.map((row, index) => (
											<tr key={row.idBlendingOrderRows || index}>
												<td>{index + 1}</td>
												<td>{row.itemName}</td>
												<td>{row.categoryName || '-'}</td>
												<td>{row.isQcAdded ? '-' : formatDecimalQuantity(row.percentage)}</td>
												<td>{row.isQcAdded ? '-' : formatDecimalQuantity(row.quantity)}</td>
												<td>{row.unitName || '-'}</td>
												<td>{row.warehouseName}</td>
												<td>{formatDecimalQuantity(displayActualQuantity(row))}</td>
												<td>{variance(row)}</td>
												<td>{variancePercent(row) === '' ? '' : variancePercent(row) + '%'}</td>
											</tr>
										))}
									</tbody>
								</Table>
							}

							{activeTab === 'qc' &&
								<Table size="sm" bordered hover>
									<thead>
										<tr>
											<th>SN</th>
											<th>Test</th>
											<th>Method</th>
											<th>Limit From</th>
											<th>Limit To</th>
											<th>Result</th>
											<th>Note</th>
										</tr>
									</thead>
									<tbody>
										{qcTests.length === 0 && (
											<tr>
												<td colSpan={7} className="text-center">No QC tests configured for this project.</td>
											</tr>
										)}
										{qcTests.map((row, index) => (
											<tr key={row.idQcTests}>
												<td>{index + 1}</td>
												<td>{row.testName}</td>
												<td>{row.method}</td>
												<td>{row.limitFrom}</td>
												<td>{row.limitTo}</td>
												<td>{row.result}</td>
												<td>{row.note}</td>
											</tr>
										))}
									</tbody>
								</Table>
							}
						</>
					)
					: (
						<>
							<h5 className="mt-5 mb-3">Required Materials</h5>
							<Table size="sm" bordered hover>
								<thead>
									<tr>
										<th>SN</th>
										<th>Item Name</th>
										<th>Category</th>
										<th>Percentage</th>
										<th>Quantity</th>
										<th>Unit</th>
										<th>Warehouse</th>
									</tr>
								</thead>
								<tbody>
									{rows.map((row, index) => (
										<tr key={row.idBlendingOrderRows || index}>
											<td>{index + 1}</td>
											<td>{row.itemName}</td>
											<td>{row.categoryName}</td>
											<td>{formatDecimalQuantity(row.percentage)}</td>
											<td>{formatDecimalQuantity(row.quantity)}</td>
											<td>{row.unitName}</td>
											<td>{row.warehouseName}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</>
					)}

				<h5 className="mt-8 mb-5">Tracking Timeline</h5>
				{timeline.length === 0
					? <div className="text-muted">No activity recorded.</div>
					: (
						<div className="timeline">
							{timeline.map((event) => {
								const style = timelineStyle[event.type] || timelineStyle.status
								return (
									<div key={event.key} className="timeline-item">
										<div className="timeline-line w-40px"></div>
										<div className="timeline-icon symbol symbol-circle symbol-40px me-4">
											<div className={`symbol-label bg-light-${style.color}`}>
												<KTSVG path={style.icon} className={`svg-icon-2 svg-icon-${style.color}`} />
											</div>
										</div>
										<div className="timeline-content mb-8 mt-n1">
											<div className="pe-3">
												<div className="fs-6 fw-bold">
													{event.type === 'packing' && event.idPackingOrders
														? <Link to={`/packing-order-details/${event.idPackingOrders}`}>{event.title}</Link>
														: event.title}
												</div>
												{event.type === 'packing' &&
													<div className="text-gray-700 fs-7 mt-1">
														Packed {formatDecimalQuantity(event.quantity)} {order.unitName} on {event.packingDate}
													</div>
												}
												{event.type === 'edit' &&
													<div className="text-gray-700 fs-7 mt-1">
														Actual Qty: {formatDecimalQuantity(event.oldValue, 2)} &rarr; {formatDecimalQuantity(event.newValue, 2)} {order.unitName}
														{canViewCost && event.oldRatePerUnit !== null && event.newRatePerUnit !== null &&
															<> | Cost per {order.unitName}: {formatDecimalQuantity(event.oldRatePerUnit, 4)} &rarr; {formatDecimalQuantity(event.newRatePerUnit, 4)}</>
														}
														{event.reason && <div style={{ whiteSpace: 'pre-wrap' }}>Reason: {event.reason}</div>}
													</div>
												}
												<div className="text-muted fs-7 mt-1">
													{formatDateTime(event.createdAt)}{event.userName ? <> by <b>{event.userName}</b></> : null}
												</div>
											</div>
										</div>
									</div>
								)
							})}
						</div>
					)}
			</div>

			<EditActualProductionQtyModal
				order={order}
				show={showEditQty}
				onHide={() => setShowEditQty(false)}
				onSaved={loadOrder}
			/>
		</div>
	)
}

export default BlendingOrderDetails
